/**
 * Generic Sample Label — the pure row-shaping logic, ported verbatim from
 * legacy ALPACA `BTLIMS.Module.Web/Controllers/SampleRegistration/
 * PrintBarcodeLabelViewController.cs`.
 *
 * These four functions ARE the popup's business logic. They live apart from
 * `GenericSampleLabelDialog.tsx` so the component file exports a component and
 * nothing else (fast-refresh) and so the logic is unit-testable without
 * rendering.
 */

/** The two report variants of the legacy `btnGenericReportType` SingleChoiceAction. */
export const LABEL_REPORT_TYPES = ['BarcodeID Label', 'SampleID Label'] as const;
export type LabelReportType = (typeof LABEL_REPORT_TYPES)[number];

/** One grid row — the legacy `NPPrintBarcodeBottle`. */
export interface LabelRow {
  /** Client-side key (legacy `Oid = Guid.NewGuid()`). */
  key: string;
  sampleId: string;
  noOfQty: number;
  bottleNo: string;
  visualMatrix: string;
  storage: string;
}

/** The Details panel — the legacy `NPPrintBarcodeHeader`. */
export interface LabelHeader {
  jobId: string;
  noOfSamples: number;
  defaultQtyPerSample: number;
  storage: string;
  visualMatrix: string;
}

/** Legacy `ToBottleLetters` — bijective base-26: A..Z, AA, AB, ... */
export function toBottleLetters(index: number): string {
  let n = index;
  let result = '';
  do {
    result = String.fromCharCode(65 + (n % 26)) + result;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return result;
}

let seq = 0;
const newKey = () => `lbl-${++seq}`;

/** Legacy spin editors: integer, MinValue 1, AllowNull false. */
export function spin(v: string | number): number {
  const n = typeof v === 'number' ? v : parseInt(String(v).replace(/[^0-9]/g, ''), 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

/** A blank Details panel (legacy `btnRefresh_Execute` creates a fresh header). */
export const emptyLabelHeader = (jobId: string, visualMatrix: string): LabelHeader => ({
  jobId, noOfSamples: 1, defaultQtyPerSample: 1, storage: '', visualMatrix,
});

/** Legacy `GeneratePreview` — pure string generation, no database read. */
export function generatePreview(h: LabelHeader): LabelRow[] {
  const out: LabelRow[] = [];
  const jobId = h.jobId ?? '';
  for (let s = 1; s <= h.noOfSamples; s++) {
    const sampleId = `${jobId}-${String(s).padStart(3, '0')}`;
    for (let q = 0; q < h.defaultQtyPerSample; q++) {
      out.push({
        key: newKey(),
        sampleId,
        noOfQty: h.defaultQtyPerSample,
        bottleNo: toBottleLetters(q),
        visualMatrix: h.visualMatrix,
        storage: h.storage,
      });
    }
  }
  return out;
}

/** Legacy `PropagateVisualMatrix` — same-SampleID rows follow the edited row. */
export function propagateVisualMatrix(rows: LabelRow[], changed: LabelRow): LabelRow[] {
  return rows.map(r =>
    r.key !== changed.key && r.sampleId === changed.sampleId
      ? { ...r, visualMatrix: changed.visualMatrix }
      : r);
}

/** Legacy `ApplyBottleCount` — grow/trim the sample's bottles, then re-letter. */
export function applyBottleCount(rows: LabelRow[], changed: LabelRow): LabelRow[] {
  const qty = changed.noOfQty < 1 ? 1 : changed.noOfQty;
  const sampleRows = rows.filter(r => r.sampleId === changed.sampleId);
  const first = sampleRows[0];
  if (!first) return rows;

  // Trim the tail beyond `qty` (legacy removes indices qty..count-1).
  const doomed = new Set(sampleRows.slice(qty).map(r => r.key));
  let next = rows.filter(r => !doomed.has(r.key));

  // Grow — insert straight after the sample's last surviving row.
  if (qty > sampleRows.length) {
    const anchorKey = sampleRows[sampleRows.length - 1]?.key;
    const insertAt = next.findIndex(r => r.key === anchorKey) + 1;
    const added: LabelRow[] = [];
    for (let i = sampleRows.length; i < qty; i++) {
      added.push({
        key: newKey(),
        sampleId: changed.sampleId,
        noOfQty: qty,
        bottleNo: toBottleLetters(i),
        visualMatrix: first.visualMatrix,
        storage: first.storage,
      });
    }
    next = [...next.slice(0, insertAt), ...added, ...next.slice(insertAt)];
  }

  // Re-letter + sync the qty across the sample's rows.
  let letter = 0;
  return next.map(r => r.sampleId === changed.sampleId
    ? { ...r, bottleNo: toBottleLetters(letter++), noOfQty: qty }
    : r);
}
